import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { BlogHeader } from "../components/blog/BlogHeader";
import { Markdown } from "../components/blog/Markdown";

const EMPTY = { title: "", slug: "", description: "", category: "Guides", cover_image: "", content: "", status: "draft" };

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5C6B64]">{label}</Label>{children}</div>
);

export default function BlogEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  usePageMeta(isNew ? "New post — Trackr" : "Edit post — Trackr");

  useEffect(() => {
    if (isNew) return;
    api.get(`/blog/admin/posts/${id}`)
      .then(({ data }) => setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? ""])) }))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [id, isNew]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (status) => {
    setSaving(true);
    try {
      const body = { ...form, status };
      const { data } = isNew ? await api.post("/blog/admin/posts", body) : await api.put(`/blog/admin/posts/${id}`, body);
      toast.success(status === "published" ? "Published" : "Draft saved");
      if (isNew) navigate(`/blog/admin/${data.id}`, { replace: true });
      else setForm((f) => ({ ...f, status: data.status, slug: data.slug }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      <BlogHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Link to="/blog/admin" className="inline-flex items-center gap-1 text-sm text-[#5C6B64] hover:text-[#C65D47]" data-testid="blog-editor-back-link">
            <ArrowLeft className="w-4 h-4" /> All posts
          </Link>
          <div className="flex gap-2">
            <Button variant="outline" disabled={saving || !form.title} onClick={() => save("draft")} className="rounded-full" data-testid="blog-editor-save-draft-btn">
              {form.status === "published" ? "Unpublish" : "Save draft"}
            </Button>
            <Button disabled={saving || !form.title} onClick={() => save("published")} className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white" data-testid="blog-editor-publish-btn">
              {form.status === "published" ? "Update" : "Publish"}
            </Button>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <Field label="Title"><Input value={form.title} onChange={set("title")} placeholder="How to…" data-testid="blog-editor-title-input" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="URL slug"><Input value={form.slug} onChange={set("slug")} placeholder="auto from title" data-testid="blog-editor-slug-input" /></Field>
              <Field label="Category"><Input value={form.category} onChange={set("category")} data-testid="blog-editor-category-input" /></Field>
            </div>
            <Field label={`Google description (${form.description.length}/300)`}>
              <Textarea rows={2} maxLength={300} value={form.description} onChange={set("description")} placeholder="One or two sentences shown in search results" data-testid="blog-editor-description-input" />
            </Field>
            <Field label="Cover image URL"><Input value={form.cover_image} onChange={set("cover_image")} placeholder="https://…" data-testid="blog-editor-cover-input" /></Field>
            <Field label="Content (Markdown)">
              <Textarea rows={22} value={form.content} onChange={set("content")} className="font-mono text-sm" placeholder={"## A heading\n\nWrite your guide…\n\n[[CTA]]"} data-testid="blog-editor-content-input" />
            </Field>
            <p className="text-xs text-[#5C6B64]">Tip: use <code>## </code> for headings and put <code>[[CTA]]</code> on its own line to place a "Start free" box.</p>
          </div>
          <div className="rounded-xl border border-[#E5E1DB] bg-white p-8 lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] overflow-auto" data-testid="blog-editor-preview">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">Preview · {form.category}</p>
            <h1 className="font-display text-3xl tracking-tight mt-2">{form.title || "Untitled post"}</h1>
            {form.cover_image && <img src={form.cover_image} alt="" className="w-full aspect-[16/9] object-cover rounded-lg mt-6" />}
            <div className="mt-6"><Markdown content={form.content} withCta /></div>
          </div>
        </div>
      </main>
    </div>
  );
}
